import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Wordmark";
import { toast } from "sonner";
import { errorMessage } from "@/lib/api";

function GoogleButton({ label = "Continue with Google", disabled }) {
  const startGoogle = () => {
    const callback = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(callback)}`;
  };
  return (
    <button
      type="button"
      onClick={startGoogle}
      disabled={disabled}
      data-testid="google-oauth-button"
      className="flex w-full items-center justify-center gap-3 rounded-xl border border-white/10 bg-white/5 py-3 text-sm text-white transition hover:bg-white/10 disabled:opacity-60"
    >
      <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden>
        <path fill="#EA4335" d="M12 11v3.6h5.07c-.22 1.18-1.4 3.46-5.07 3.46-3.05 0-5.54-2.52-5.54-5.62s2.49-5.62 5.54-5.62c1.74 0 2.9.74 3.57 1.37l2.43-2.34C16.46 4.39 14.45 3.5 12 3.5 6.97 3.5 2.9 7.57 2.9 12.4S6.97 21.3 12 21.3c6.93 0 9.1-4.83 9.1-7.32 0-.49-.05-.86-.11-1.23H12z"/>
      </svg>
      <span>{label}</span>
    </button>
  );
}

function AuroraSide() {
  return (
    <div className="relative hidden flex-1 overflow-hidden border-l border-white/5 lg:flex">
      <div className="aurora" />
      <div className="grain" />
      <div className="relative z-10 flex h-full w-full flex-col justify-between p-12">
        <Link to="/" className="flex items-center" aria-label="Symplee — home">
          <Wordmark variant="dark" size="lg" />
        </Link>
        <div>
          <div className="text-eyebrow text-indigo-600 dark:text-indigo-300">Symplee</div>
          <h2 className="font-display mt-4 max-w-md text-4xl font-light tracking-tight">
            AI made simple<br />
            for <span className="brand-text">your business.</span>
          </h2>
          <p className="mt-6 max-w-md text-white/60">
            Save time and money on the busywork — without rebuilding everything you've worked hard to create.
          </p>
        </div>
        <p className="text-xs text-white/40">
          One simple price · No contracts · Fully reversible
        </p>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(form.email, form.password);
      toast.success("Welcome back.");
      navigate("/app");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#050505] text-white">
      <div className="flex min-h-screen">
        <div className="relative flex flex-1 items-center justify-center p-6 sm:p-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="w-full max-w-md"
          >
            <Link to="/" className="mb-10 inline-flex items-center lg:hidden" data-testid="auth-logo" aria-label="Symplee — home">
              <Wordmark variant="dark" size="lg" />
            </Link>
            <h1 className="font-display text-4xl font-light tracking-tight sm:text-5xl">Welcome back.</h1>
            <p className="mt-3 text-white/55">Sign in to your workspace.</p>

            <div className="mt-10 space-y-4">
              <GoogleButton disabled={submitting} />
              <div className="relative my-2 flex items-center gap-3 text-xs text-white/40">
                <div className="h-px flex-1 bg-white/10" /> or with email <div className="h-px flex-1 bg-white/10" />
              </div>
              <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-white/50">Email</Label>
                  <Input
                    type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="border-white/10 bg-black/40 text-white"
                    data-testid="login-email"
                    placeholder="you@company.com"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-wider text-white/50">Password</Label>
                    <Link to="/forgot-password" className="text-xs text-white/50 hover:text-white" data-testid="forgot-password-link">
                      Forgot?
                    </Link>
                  </div>
                  <Input
                    type="password" required value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="border-white/10 bg-black/40 text-white"
                    data-testid="login-password"
                    placeholder="••••••••"
                  />
                </div>
                <Button type="submit" disabled={submitting} className="h-11 w-full rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="login-submit">
                  {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Sign in <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </form>

              <p className="text-center text-sm text-white/55">
                New here?{" "}
                <Link to="/signup" className="text-white underline-offset-4 hover:underline" data-testid="login-to-signup-link">
                  Start your free trial
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
        <AuroraSide />
      </div>
    </div>
  );
}

export { GoogleButton, AuroraSide };
